import type { GeometryData } from '@/context/geometry';
import type {
  MunicipalitiesComparative,
  MunicipalityComparativeDimension,
} from '@/graphql/types/municipality';
import { Classification, ClassificationOpportunityAndRisk } from '@/types/classification';

const DEFAULT_INTENSITY_RISK = '#fe9b33';
const DEFAULT_INTENSITY_OPPORTUNITY = '#116938';

function parseClassification(value: string | undefined): ClassificationOpportunityAndRisk {
  if (!value) return ClassificationOpportunityAndRisk.LOW;
  const upper = value.toUpperCase().replace(/-/g, '_');
  if (upper in ClassificationOpportunityAndRisk) {
    return ClassificationOpportunityAndRisk[upper as keyof typeof ClassificationOpportunityAndRisk];
  }
  return ClassificationOpportunityAndRisk.LOW;
}

function getDimensionSlice(
  row: MunicipalitiesComparative,
  dimensionId: number
): MunicipalityComparativeDimension | undefined {
  const direct = row.dimensions[dimensionId];
  if (direct) return direct;
  return Object.values(row.dimensions).find((d) => d.dimension.id === dimensionId);
}

function firstDimensionByOrder(
  row: MunicipalitiesComparative
): MunicipalityComparativeDimension | undefined {
  const list = Object.values(row.dimensions);
  if (list.length === 0) return undefined;
  return [...list].sort((a, b) => a.dimension.order - b.dimension.order)[0];
}

function rowToGeometryOverall(row: MunicipalitiesComparative): GeometryData {
  const m = row.municipality;
  const firstDim = firstDimensionByOrder(row);
  const intensityRisk = firstDim?.intensity_color_risk?.trim() || DEFAULT_INTENSITY_RISK;
  const intensityOpportunity =
    firstDim?.intensity_color_opportunity?.trim() || DEFAULT_INTENSITY_OPPORTUNITY;

  return {
    municipality_code: m.municipality_code,
    code_uf: m.code_uf,
    name_municipality: m.name_municipality,
    name_uf: m.name_uf,
    sigla_uf: m.sigla_uf,
    biome: m.biome ?? '',
    center_point: [],
    name_region: m.name_region,
    population: m.population ?? 0,
    classification_type: m.classification_type ?? Classification.RURAL,
    dimensions_scores: [],
    municipality_score: {
      general_score_risk: row.score.risk ?? '0',
      general_score_opportunity: row.score.opportunity ?? '0',
      intensity_color_risk: intensityRisk,
      intensity_color_opportunity: intensityOpportunity,
      classification_risk: parseClassification(row.classification.risk),
      classification_opportunity: parseClassification(row.classification.opportunity),
    },
  };
}

function rowToGeometryForDimension(
  row: MunicipalitiesComparative,
  dimensionId: number
): GeometryData {
  const m = row.municipality;
  const slice = getDimensionSlice(row, dimensionId);
  if (!slice) {
    return rowToGeometryOverall(row);
  }

  const classificationRisk = parseClassification(slice.classification_risk);
  const classificationOpportunity = parseClassification(slice.classification_opportunity);
  const intensityRisk = slice.intensity_color_risk?.trim() || DEFAULT_INTENSITY_RISK;
  const intensityOpportunity =
    slice.intensity_color_opportunity?.trim() || DEFAULT_INTENSITY_OPPORTUNITY;

  return {
    municipality_code: m.municipality_code,
    code_uf: m.code_uf,
    name_municipality: m.name_municipality,
    name_uf: m.name_uf,
    sigla_uf: m.sigla_uf,
    biome: m.biome ?? '',
    center_point: [],
    name_region: m.name_region,
    population: m.population ?? 0,
    classification_type: m.classification_type ?? Classification.RURAL,
    dimensions_scores: [
      {
        score_risk: slice.municipality_risk ?? '0',
        score_opportunity: slice.municipality_opportunity ?? '0',
        dimension: {
          id: slice.dimension.id,
          name: slice.dimension.name,
        },
        intensity_color_opportunity: intensityOpportunity,
        intensity_color_risk: intensityRisk,
        classification_risk: classificationRisk,
        classification_opportunity: classificationOpportunity,
      },
    ],
    municipality_score: {
      general_score_risk: slice.municipality_risk ?? '0',
      general_score_opportunity: slice.municipality_opportunity ?? '0',
      intensity_color_risk: intensityRisk,
      intensity_color_opportunity: intensityOpportunity,
      classification_risk: classificationRisk,
      classification_opportunity: classificationOpportunity,
    },
  };
}

export function comparativeRowsToMapGeometry(
  rows: MunicipalitiesComparative[],
  dimensionId: number
): GeometryData[] {
  if (dimensionId === 0) {
    return rows.map(rowToGeometryOverall);
  }
  return rows.map((row) => rowToGeometryForDimension(row, dimensionId));
}
